import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { FEATURE_ACCESS } from '../config/subscriptionFeatures';
import LoadingSpinner from './ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireSubscription?: boolean;
  requiredFeatures?: string[];
}

/**
 * Guards routes by auth state and subscription plan.
 * Uses SubscriptionContext (the real subscription source) - not the legacy useUserStore.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireSubscription = false,
  requiredFeatures = [],
}) => {
  const { user, loading: authLoading } = useAuth();
  const { subscription, loading: subLoading } = useSubscription();
  const location = useLocation();

  if (authLoading || (requireSubscription && subLoading)) {
    return <LoadingSpinner />;
  }

  if (requireAuth && !user) {
    if (requireSubscription || requiredFeatures.length > 0) {
      return (
        <Navigate
          to="/pricing"
          state={{
            from: location,
            missingFeatures: requiredFeatures,
            reason: 'auth-required',
          }}
          replace
        />
      );
    }

    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (requireSubscription && subscription?.status !== 'active') {
    return (
      <Navigate
        to="/pricing"
        state={{
          from: location,
          missingFeatures: requiredFeatures,
          reason: 'inactive-subscription',
        }}
        replace
      />
    );
  }

  if (requiredFeatures.length > 0) {
    const plan = subscription?.plan ?? 'basic';
    const planFeatures: string[] = FEATURE_ACCESS[plan] ?? [];
    const missingFeatures = requiredFeatures.filter(f => !planFeatures.includes(f));

    if (missingFeatures.length > 0) {
      return <Navigate to="/pricing" state={{ from: location, missingFeatures }} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
